import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import Cookies from 'cookies';
import Api from '../src/api';
import { VehicleInfo } from '../src/types/vehicleInfo';

const VEHICLE_INFO_COOKIE_NAME = 'cvna-landing-vehicle';
const COOKIE_MAX_AGE = 48 * 60 * 60; // 48 hours in seconds

const getVehicleInfoCookieOptions = (
  isSecure: boolean,
  deleteCookie: boolean = false
): Partial<Cookies.SetOption> => ({
  maxAge: deleteCookie ? 0 : COOKIE_MAX_AGE * 1000, // cookies package expects milliseconds
  path: '/',
  sameSite: 'lax',
  secure: isSecure,
  httpOnly: false
});

const getVehicleDisplayName = (
  year: string | undefined,
  make: string,
  model: string | undefined
): string => {
  // Display name must reflect what was searched for by the user (ignoring default values)
  if (year && make && model) {
    return `${year} ${make} ${model}`;
  }

  if (year && make) {
    return `${year} ${make}`;
  }

  if (make && model) {
    return `${make} ${model}`;
  }

  return make;
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { req, res, query } = context;

  // Determine if the connection is secure (HTTPS)
  // Trust the x-forwarded-proto header from the proxy (nginx)
  const forwardedProto = req.headers['x-forwarded-proto'];
  const isSecure = forwardedProto === 'https';

  // Pass secure flag to cookies library to trust proxy headers
  const cookies = new Cookies(req, res, { secure: isSecure });

  const { year, make, model } = query;
  const sanitizedYear = year && (decodeURIComponent(year as string) as string);
  const sanitizedMake = make && (decodeURIComponent(make as string) as string);
  const sanitizedModel = model && (decodeURIComponent(model as string) as string);

  const vehicleInfo: VehicleInfo = {
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleDisplayName: 'Car',
    vehicleDesktopImageUrl: '',
    vehicleMobileImageUrl: '',
    isExactImageMatch: false,
    searchYear: '',
    searchModel: ''
  };

  if (sanitizedMake) {
    const customizedVehicleInfo = await Api.bff.getLandingPageInfo({
      year: sanitizedYear,
      make: sanitizedMake,
      model: sanitizedModel
    });

    if (
      customizedVehicleInfo &&
      customizedVehicleInfo.data &&
      (customizedVehicleInfo.data.desktopImageUrl || customizedVehicleInfo.data.mobileImageUrl)
    ) {
      vehicleInfo.vehicleYear = customizedVehicleInfo.data.year;
      vehicleInfo.vehicleMake = customizedVehicleInfo.data.make;
      vehicleInfo.vehicleModel = customizedVehicleInfo.data.model;
      vehicleInfo.vehicleDisplayName = getVehicleDisplayName(
        sanitizedYear && customizedVehicleInfo.data.year,
        customizedVehicleInfo.data.make,
        sanitizedModel && customizedVehicleInfo.data.model
      );
      vehicleInfo.vehicleDesktopImageUrl = customizedVehicleInfo.data.desktopImageUrl;
      vehicleInfo.vehicleMobileImageUrl = customizedVehicleInfo.data.mobileImageUrl;

      vehicleInfo.isExactImageMatch = customizedVehicleInfo.data.isExactImageMatch;
      vehicleInfo.searchYear = sanitizedYear || '';
      vehicleInfo.searchModel = sanitizedModel || '';

      cookies.set(
        VEHICLE_INFO_COOKIE_NAME,
        JSON.stringify(vehicleInfo),
        getVehicleInfoCookieOptions(isSecure)
      );
    }
  } else {
    const cookieValue = cookies.get(VEHICLE_INFO_COOKIE_NAME);
    let parsedVehicleInfo: VehicleInfo | null = null;

    if (cookieValue) {
      try {
        parsedVehicleInfo = JSON.parse(cookieValue) as VehicleInfo;
      } catch {
        // Invalid JSON, ignore
      }
    }

    if (
      parsedVehicleInfo &&
      (parsedVehicleInfo.vehicleDesktopImageUrl || parsedVehicleInfo.vehicleMobileImageUrl)
    ) {
      Object.assign(vehicleInfo, parsedVehicleInfo);

      // Refresh cookie to reset 48-hour expiration
      cookies.set(
        VEHICLE_INFO_COOKIE_NAME,
        JSON.stringify(parsedVehicleInfo),
        getVehicleInfoCookieOptions(isSecure)
      );
    }
  }

  return { props: { vehicleInfo } };
};

const Marketing = dynamic(() => import('../src/components/Marketing/Marketing'));

const HomePage = ({ vehicleInfo }: InferGetServerSidePropsType<typeof getServerSideProps>) => (
  <Marketing customVehicleInfo={vehicleInfo} />
);

export default HomePage;
